import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

export const Route = createFileRoute('/_site/docs')({
  component: DocsPage,
  head: () => ({
    meta: [
      { title: 'API Documentation — Uptrack Uptime Monitoring' },
      { name: 'description', content: 'Interactive API documentation for Uptrack uptime monitoring. Create monitors, manage incidents, and integrate with your workflow.' },
      { property: 'og:title', content: 'API Docs — Uptrack' },
      { property: 'og:description', content: 'Interactive API documentation for Uptrack uptime monitoring. Create monitors, manage incidents, and integrate with your workflow.' },
      { property: 'og:image', content: 'https://uptrack.app/og/docs.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/docs' },
    ],
  }),
})

// Swagger UI runs on the uptrack.app origin, so it is pinned to one version and checked with
// Subresource Integrity: a changed or hijacked file does not run. To upgrade, change the version
// and both hashes (`openssl dgst -sha384 -binary <file> | openssl base64 -A`).
const SWAGGER_VERSION = '5.33.0'
const SWAGGER_CSS_SRI = 'sha384-Ov4/wv3j2bmct8cDc5X4ngJZohVPzEmc6uDPH8WeljUxO5vtoykvMEfbu9Vh6RaW'
const SWAGGER_JS_SRI = 'sha384-YDALVcy8kj8yltLBVi1vBiBAUqdxvus673gM8XKwiy6aDUJFXivF/KCufekjYbVf'

// Swagger UI ships colors under 4.5:1 and its own <main>. Scoped fixes, kept here so they
// travel with the embed. HTTP method badges use darker shades with white text (>= 4.5:1).
const SWAGGER_A11Y_CSS = `
.swagger-ui-wrapper .swagger-ui .opblock.opblock-get .opblock-summary-method { background: #1F5AD6; }
.swagger-ui-wrapper .swagger-ui .opblock.opblock-post .opblock-summary-method { background: #2E7D4F; }
.swagger-ui-wrapper .swagger-ui .opblock.opblock-put .opblock-summary-method { background: #8A5A00; }
.swagger-ui-wrapper .swagger-ui .opblock.opblock-patch .opblock-summary-method { background: #1C7268; }
.swagger-ui-wrapper .swagger-ui .opblock.opblock-delete .opblock-summary-method { background: #B03B1F; }
.swagger-ui-wrapper .swagger-ui .btn.authorize { color: #2E7D4F; border-color: #2E7D4F; }
.swagger-ui-wrapper .swagger-ui .btn.authorize span, .swagger-ui-wrapper .swagger-ui .btn.authorize svg { color: #2E7D4F; fill: #2E7D4F; }
/* Swagger UI sets generic sans-serif / monospace on its own elements. Use the site fonts. */
.swagger-ui-wrapper .swagger-ui * { font-family: var(--font-sans) !important; }
.swagger-ui-wrapper .swagger-ui pre, .swagger-ui-wrapper .swagger-ui code, .swagger-ui-wrapper .swagger-ui textarea,
.swagger-ui-wrapper .swagger-ui .microlight, .swagger-ui-wrapper .swagger-ui .opblock-summary-path,
.swagger-ui-wrapper .swagger-ui .opblock-summary-path__deprecated, .swagger-ui-wrapper .swagger-ui .parameter__name,
.swagger-ui-wrapper .swagger-ui .parameter__type, .swagger-ui-wrapper .swagger-ui .prop-type { font-family: var(--font-mono) !important; }
`

/** Swagger renders a second main landmark and h4 tag headings under our h1; fix both. */
function fixSwaggerLandmarks(root: HTMLElement) {
  root.querySelectorAll('main').forEach((el) => {
    el.setAttribute('role', 'region')
    el.setAttribute('aria-label', 'API operations')
  })
  root.querySelectorAll('.opblock-tag').forEach((el) => {
    el.setAttribute('role', 'heading')
    el.setAttribute('aria-level', '2')
  })
  root.querySelectorAll('.opblock-summary-path, .opblock-summary-description').forEach((el) => {
    if (/^H[1-6]$/.test(el.tagName)) {
      el.setAttribute('role', 'heading')
      el.setAttribute('aria-level', '3')
    }
  })
}

function DocsPage() {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Load Swagger UI from CDN
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://cdn.jsdelivr.net/npm/swagger-ui-dist@${SWAGGER_VERSION}/swagger-ui.css`
    link.integrity = SWAGGER_CSS_SRI
    link.crossOrigin = 'anonymous'
    document.head.appendChild(link)
    const style = document.createElement('style')
    style.textContent = SWAGGER_A11Y_CSS
    document.head.appendChild(style)

    // Swagger re-renders as sections open; re-apply the landmark fixes each time.
    const root = containerRef.current
    const observer = new MutationObserver(() => root && fixSwaggerLandmarks(root))
    if (root) observer.observe(root, { childList: true, subtree: true })

    const script = document.createElement('script')
    script.src = `https://cdn.jsdelivr.net/npm/swagger-ui-dist@${SWAGGER_VERSION}/swagger-ui-bundle.js`
    script.integrity = SWAGGER_JS_SRI
    script.crossOrigin = 'anonymous'
    script.onload = () => {
      if (containerRef.current && (window as any).SwaggerUIBundle) {
        ;(window as any).SwaggerUIBundle({
          // The Rust API serves the spec at /openapi.json (the Phoenix /api/openapi path 404s).
          url: 'https://api.uptrack.app/openapi.json',
          domNode: containerRef.current,
          deepLinking: true,
          presets: [(window as any).SwaggerUIBundle.presets.apis],
          layout: 'BaseLayout',
          // The page has its own h1. Swagger's info block (its title is a second h1) was
          // already hidden with CSS; drop it from the DOM so the page has exactly one h1.
          plugins: [() => ({ wrapComponents: { InfoContainer: () => () => null } })],
          defaultModelsExpandDepth: -1,
          docExpansion: 'list',
          filter: true,
          tryItOutEnabled: false,
        })
      }
    }
    document.body.appendChild(script)

    return () => {
      observer.disconnect()
      document.head.removeChild(style)
      document.head.removeChild(link)
      document.body.removeChild(script)
    }
  }, [])

  return (
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="mb-8">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">API Documentation</h1>
            <p className="mt-4 max-w-2xl text-lead text-body">
              Full REST API access on every paid plan. Authenticate with{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">Authorization: Bearer YOUR_API_KEY</code>{' '}
              — get your key at{' '}
              <a href="https://uptrack.app/dashboard/settings" className="text-primary hover:underline">Dashboard → Settings</a>.
            </p>
            <p className="mt-3 max-w-2xl text-body">
              Using Claude or another AI assistant? Connect it with the{' '}
              <Link to="/guides/mcp" className="text-primary hover:underline">MCP server guide</Link>.
            </p>
          </div>

          {/* Swagger UI container — override dark theme */}
          <div
            ref={containerRef}
            className="swagger-ui-wrapper rounded-lg border border-border bg-white p-4 [&_.swagger-ui]:text-sm [&_.swagger-ui_.info]:hidden"
          />
        </div>
  )
}
